import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import './Navbar.css';

const Navbar = ({ disableLanguageSelector = false }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'uk', name: 'Українська', flag: '🇺🇦' },
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Fermer le dropdown quand on clique à l'extérieur
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLanguageDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (languageCode) => {
    i18n.changeLanguage(languageCode);
    localStorage.setItem('language', languageCode);
    setShowLanguageDropdown(false);
  };

  // Mapper les chemins aux titres de pages
  const getPageTitle = () => {
    const path = location.pathname;
    
    // Gérer les routes avec paramètres
    if (path.startsWith('/matrice/edit/')) {
      return t('matrice.title') || 'Modifier une matrice';
    }
    if (path.startsWith('/document/edit/')) {
      return t('document.title') || 'Modifier un document';
    }
    
    switch (path) {
      case '/dashboard':
        return t('dashboard.title') || 'Dashboard';
      case '/profile':
        return t('profile.title') || 'Mon profil';
      case '/matrice':
        return t('matrice.title') || 'Créer une matrice';
      case '/document':
        return t('document.title') || 'Créer un document';
      case '/history':
        return t('history.title') || 'Historique';
      case '/document-history':
        return t('documentHistory.title') || 'Historique des documents';
      default:
        return '';
    }
  };

  return (
    <nav className="navbar">
      <div className="navbar-left">
        <h1 
          className="navbar-logo" 
          onClick={() => navigate(user ? '/dashboard' : '/')}
          role="button"
          tabIndex={0}
          onKeyPress={(e) => e.key === 'Enter' && navigate(user ? '/dashboard' : '/')}
        >
          Synestesia
        </h1>
        {location.pathname !== '/dashboard' && (
          <span className="navbar-page-title">{getPageTitle()}</span>
        )}
      </div>

      <div className="navbar-right">
        {user && (
          <button
            className="navbar-profile-button"
            onClick={() => navigate('/profile')}
            title={t('profile.title') || 'Mon profil'}
            aria-label={t('profile.title') || 'Mon profil'}
          >
            {t('profile.button') || 'Profil'}
          </button>
        )}
        
        {/* Sélecteur de langue */}
        {!disableLanguageSelector && (
          <div className="navbar-language-selector" ref={dropdownRef}>
            <button 
              className="navbar-language-button"
              onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
              aria-label="Changer de langue"
              title="Changer de langue"
            >
              <span className="navbar-language-flag">{currentLanguage.flag}</span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            
            {showLanguageDropdown && (
              <div className="navbar-language-dropdown">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    className={`navbar-language-option ${lang.code === i18n.language ? 'active' : ''}`}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    <span className="navbar-language-option-flag">{lang.flag}</span>
                    <span className="navbar-language-option-name">{lang.name}</span>
                    {lang.code === i18n.language && (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        
        <button 
          className="navbar-theme-toggle"
          onClick={toggleTheme}
          aria-label={isDarkMode ? 'Activer le mode clair' : 'Activer le mode sombre'}
          title={isDarkMode ? 'Mode clair' : 'Mode sombre'}
        >
          {isDarkMode ? (
            // Icône soleil (mode clair)
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/>
              <line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            // Icône lune (mode sombre)
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </button>

        {user && (
          <button 
            className="navbar-logout"
            onClick={logout}
            title={t('auth.logout')}
            aria-label={t('auth.logout')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
          </button>
        )}
      </div>
    </nav>
  );
};

export default Navbar;

